<template>
  <li
    :id="`message${data.id}`"
    ref="message"
    :class="{
      'justify-start': isIncoming,
      'justify-end': isOutgoing || isTemplate,
      'justify-center': isActivity,
    }"
  >
    <!-- Activity / System message -->
    <system-message
      v-if="isActivity"
      :message-content="message"
      :message-data="data"
    />
    <!-- Chat bubble -->
    <template v-else-if="shouldRenderMessage">
      <div class="flex flex-col w-full gap-2">
        <div
          class="flex flex-row items-center"
          :class="isIncoming ? 'flex-row' : 'flex-row-reverse'"
        >
          <div
            class="group relative flex justify-end items-end gap-2 w-fit"
            :class="isIncoming ? 'flex-row-reverse' : 'flex-row'"
            @contextmenu.prevent="openContextMenu($event)"
          >
            <!-- More button -->
            <button
              v-if="shouldShowContextMenu"
              class="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-[4px] text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity duration-150 cursor-pointer hover:bg-gray-200"
              @click.stop="openMoreMenu($event)"
            >
              <span class="material-symbols-outlined text-icon-primary">
                more_horiz
              </span>
            </button>

            <!-- Message bubble -->
            <div
              class="flex flex-col gap-1 rounded-xl p-3 border"
              :class="[
                data.private
                  ? 'bg-yellow-50 dark:bg-yellow-800 dark:border-yellow-600'
                  : isIncoming
                  ? 'bg-white dark:bg-slate-700 dark:border-slate-600'
                  : isBotMessage
                  ? 'bubble--bot bg-[#A855F7] dark:bg-[#9333EA]'
                  : 'bg-green-50 dark:bg-green-950 dark:border-slate-700',
                {
                  'opacity-60': isPending,
                  'border-[#A855F7] dark:border-[#9333EA]':
                    isBotMessage && !showContextMenu,
                  'border-[#7E22CE]': isBotMessage && showContextMenu,
                  'border-green-400':
                    showContextMenu && !isIncoming && !isBotMessage,
                  'border-gray-200': showContextMenu && isIncoming,
                  'border-neutral-100':
                    !showContextMenu && !data.private && !isBotMessage,
                  'border-yellow-200': data.private,
                },
              ]"
              style="max-width: Min(31rem, 84%)"
            >
              <bubble-reply
                :reply-data="contentAttributes.reply_data"
                :data="data"
                :is-from-bot="isSentByBot"
              />
              <bubble-referral
                v-if="data.content_type === 'referral'"
                :referral-data="contentAttributes.referral_info"
                :data="data"
                :confirm-open-external-link="onConfirmOpenLink"
              />
              <bubble-contact-info
                v-if="data.content_type === 'contact_info'"
                :name="contentAttributes.contact_info.name"
                :phone-number="contentAttributes.contact_info.number"
                :email="contentAttributes.contact_info.email"
              />

              <div class="message-text__wrap">
                <!--d99d-->
                <strong
                  v-if="isIncoming && contentAttributes.story_sender"
                  class="text-sm"
                >
                  {{ contentAttributes.story_sender }}
                  <span
                    v-if="contentAttributes.story_id"
                    class="text-xs font-light"
                  >
                    {{ `(${contentAttributes.story_id})` }}
                  </span>
                </strong>
              </div>

              <div v-if="storyReply" class="mb-1.5">
                <div
                  class="bg-neutral-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-[12px] py-1.5 ltr:pl-3 ltr:pr-2 rtl:pr-3 rtl:pl-2 flex items-start gap-2 overflow-hidden"
                >
                  <div
                    class="self-stretch w-0.5 bg-green-300 flex-shrink-0 mt-0.5"
                  />
                  <div class="flex-grow min-w-0 flex flex-col gap-1">
                    <span class="text-xs font-semibold text-green-700">
                      {{ $t('CONVERSATION.REPLIED_TO_STORY') }}
                    </span>
                    <bubble-image
                      v-if="!hasImgStoryError && storyUrl"
                      :url="storyUrl"
                      @error="onStoryLoadError"
                    />
                    <bubble-video
                      v-else-if="hasImgStoryError && storyUrl"
                      :url="storyUrl"
                    />
                  </div>
                </div>
              </div>

              <card-message
                v-if="data.content_type === 'cards'"
                :items="contentAttributes.items"
              />

              <!-- Text content -->
              <div
                v-if="data.content"
                class="message-text text-sm text-gray-800 dark:text-slate-100 wrap-anywhere leading-5"
                dir="auto"
                v-html="message"
              />
              <div v-if="!isPending && hasAttachments">
                <div v-for="attachment in attachments" :key="attachment.id">
                  <bubble-image-audio-video
                    v-if="isAttachmentImageVideoAudio(attachment.file_type)"
                    :attachment="attachment"
                    @error="onImageLoadError"
                  />
                  <bubble-location
                    v-else-if="attachment.file_type === 'location'"
                    :latitude="attachment.coordinates_lat"
                    :longitude="attachment.coordinates_long"
                    :name="attachment.fallback_title"
                  />
                  <bubble-contact
                    v-else-if="attachment.file_type === 'contact'"
                    :name="data.content"
                    :phone-number="attachment.fallback_title"
                  />
                  <instagram-image-error-placeholder
                    v-else-if="hasImageError && hasInstagramStory"
                  />
                  <bubble-file
                    v-else
                    :url="attachment.data_url"
                    :file_type="attachment.file_type"
                  />
                </div>
              </div>

              <bubble-buttons
                v-if="data.content_type === 'buttons'"
                :configs="contentAttributes.buttons_info"
              />

              <bubble-integration
                :message-id="data.id"
                :content-attributes="contentAttributes"
                :inbox-id="data.inbox_id"
              />
              <div
                v-if="isPending && hasAttachments"
                class="chat-bubble has-attachment agent text-gray-400 text-sm flex flex-row items-center gap-1 mt-2"
              >
                <svg
                  class="animate-spin text-gray-400 w-3 h-3"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {{ $t('CONVERSATION.UPLOADING_ATTACHMENTS') }}
              </div>
              <!-- Blocked attachments alert -->
              <div
                v-if="contentAttributes.attachment_errors"
                class="text-sm text-red-500"
              >
                {{ $t('CONVERSATION.BLOCKED_ATTACHMENTS') }}
              </div>

              <p
                v-if="targetLanguage && translatedMessage"
                dir="ltr"
                class="translate mb-0"
              >
                {{ translatedMessage }}
                <a href="#" @click.prevent="handleHideTranslate">
                  {{ $t('CONVERSATION.HIDE_TRANSLATION') }}
                </a>
              </p>

              <message-actions
                :id="data.id"
                :sender="data.sender"
                :story-sender="storySender"
                :external-error="externalError"
                :story-id="`${storyId}`"
                :is-a-tweet="isATweet"
                :is-a-whatsapp-channel="isAWhatsAppChannel"
                :has-instagram-story="hasInstagramStory"
                :is-private="data.private"
                :message-type="data.message_type"
                :message-status="status"
                :source-id="data.source_id"
                :inbox-id="data.inbox_id"
                :created-at="createdAt"
                :status-history="data.status_history"
                :delay-hours="data.delay_hours"
                :is-liked="isLiked"
                :reaction="reaction"
              />
            </div>

            <!-- Agent avatar (outgoing / template), on the outer side of the bubble -->
            <div
              v-if="showAgentAvatar"
              v-tooltip.top="tooltipForSender"
              class="flex-shrink-0"
            >
              <woot-thumbnail
                :src="sender.thumbnail || sender.avatar_url || ''"
                :username="senderNameForAvatar"
                size="24px"
                class="shrink-0"
              />
            </div>

            <!-- Pending spinner -->
            <svg
              v-if="isPending"
              class="mr-3 -ml-1 size-5 w-4 animate-spin text-gray-700"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>

          <!-- Context menu -->
          <div v-if="shouldShowContextMenu" class="context-menu-wrap">
            <context-menu
              v-if="!isMessageDeleted"
              :context-menu-position="contextMenuPosition"
              :is-open="showContextMenu"
              :d99d-enables="d99dEnables"
              :enabled-options="contextMenuEnabledOptions"
              :message="data"
              :show-trigger-button="false"
              @open="openContextMenu"
              @close="closeContextMenu"
              @remoteAssignFile="handleRemoteAssignFile"
              @d99dSelect="handleD99dSelection"
              @replyTo="handleReplyTo"
              @translate="handleShowTranslate"
            />
          </div>
        </div>

        <div
          v-if="contentAttributes.attachment_errors"
          class="flex justify-center"
        >
          <system-message
            :message-content="$t('CONVERSATION.MESSAGE.ATTACHMENT_ERROR_MESSAGE')"
            :message-data="data"
          />
        </div>
      </div>
    </template>
    <!-- Confirm external link modal -->
    <woot-delete-modal
      :show.sync="showLinkConfirmModal"
      :on-close="onCancelOpenLink"
      :on-confirm="onConfirmOpenLinkAction"
      :title="$t('CONVERSATION.CONFIRM_LINK.TITLE')"
      :message="$t('CONVERSATION.CONFIRM_LINK.MESSAGE')"
      :confirm-text="$t('CONVERSATION.CONFIRM_LINK.CONFIRM')"
      :reject-text="$t('CONVERSATION.CONFIRM_LINK.CANCEL')"
    />
  </li>
</template>

<script>
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import timeMixin from 'dashboard/mixins/time';
import Spinner from 'shared/components/Spinner.vue';
import ContextMenu from 'dashboard/routes/dashboard/conversation/MessageContextMenu.vue';
import alertMixin from 'shared/mixins/alertMixin';
import contentTypeMixin from 'shared/mixins/contentTypeMixin';
import { MESSAGE_TYPE, MESSAGE_STATUS } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import SystemMessage from '../SystemMessage.vue';
import { mapGetters } from 'vuex';
import BubbleReply from 'dashboard/components/widgets/conversation/bubble/Reply';
import BubbleReferral from 'dashboard/components/widgets/conversation/bubble/Referral';
import BubbleContactInfo from 'dashboard/components/widgets/conversation/bubble/ContactInfo.vue';
import BubbleImage from 'dashboard/components/widgets/conversation/bubble/Image.vue';
import BubbleVideo from 'dashboard/components/widgets/conversation/bubble/Video.vue';
import BubbleImageAudioVideo from 'dashboard/components/widgets/conversation/bubble/ImageAudioVideo.vue';
import BubbleLocation from 'dashboard/components/widgets/conversation/bubble/Location.vue';
import BubbleContact from 'dashboard/components/widgets/conversation/bubble/Contact.vue';
import BubbleFile from 'dashboard/components/widgets/conversation/bubble/File.vue';
import BubbleButtons from 'dashboard/components/widgets/conversation/bubble/Buttons.vue';
import BubbleIntegration from 'dashboard/components/widgets/conversation/bubble/Integration.vue';
import instagramImageErrorPlaceholder from 'dashboard/components/widgets/conversation/instagramImageErrorPlaceholder.vue';
import CardMessage from 'dashboard/components/widgets/conversation/CardMessage.vue';
import MessageActions from './MessageActions';

export default {
  components: {
    ContextMenu,
    Spinner,
    SystemMessage,
    BubbleReply,
    BubbleReferral,
    BubbleContactInfo,
    BubbleImage,
    BubbleVideo,
    BubbleImageAudioVideo,
    BubbleLocation,
    BubbleContact,
    BubbleFile,
    BubbleButtons,
    BubbleIntegration,
    MessageActions,
    instagramImageErrorPlaceholder,
    CardMessage,
  },
  mixins: [
    alertMixin,
    timeMixin,
    messageFormatterMixin,
    contentTypeMixin,
    hasPermissionMixin,
  ],
  props: {
    data: {
      type: Object,
      required: true,
    },
    isATweet: {
      type: Boolean,
      default: false,
    },
    deleteable: {
      type: Boolean,
      default: false,
    },
    assignable: {
      type: Boolean,
      default: false,
    },
    isAWhatsAppChannel: {
      type: Boolean,
      default: false,
    },
    isATiktok: {
      type: Boolean,
      default: false,
    },
    hasInstagramStory: {
      type: Boolean,
      default: false,
    },
    isWebWidgetInbox: {
      type: Boolean,
      default: false,
    },
    d99dFeatures: {
      type: Object,
      default: () => ({}),
    },
    inboxSupportsReplyTo: {
      type: Boolean,
      default: false,
    },
    inReplyTo: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      showLinkConfirmModal: false,
      pendingLinkHref: null,
      showContextMenu: false,
      contextMenuPosition: {},
      d99dEnables: { empty: true },
      hasImageError: false,
      hasImgStoryError: false,
      targetLanguage: null,
      showBackgroundHighlight: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
    }),
    isActivity() {
      return this.data.message_type === MESSAGE_TYPE.ACTIVITY;
    },
    isIncoming() {
      return this.data.message_type === MESSAGE_TYPE.INCOMING;
    },
    isOutgoing() {
      return this.data.message_type === MESSAGE_TYPE.OUTGOING;
    },
    isTemplate() {
      return this.data.message_type === MESSAGE_TYPE.TEMPLATE;
    },
    isPending() {
      return this.data.status === MESSAGE_STATUS.PROGRESS;
    },
    isFailed() {
      return false;
    },
    isMessageDeleted() {
      return this.contentAttributes.deleted;
    },
    contentType() {
      return this.data.content_type || '';
    },
    attachments() {
      return this.data?.attachments.map(attachment => ({
        ...attachment,
        sender: this.data.sender || {},
        created_at: this.data.created_at || '',
      }));
    },
    hasAttachments() {
      return !!(this.data.attachments && this.data.attachments.length > 0);
    },
    shouldRenderMessage() {
      return (
        this.hasAttachments ||
        this.data.content ||
        this.isAnIntegrationMessage ||
        ['referral', 'buttons', 'contact', 'contact_info', 'cards'].includes(
          this.contentType
        ) ||
        this.contentAttributes.attachment_errors // d99d
      );
    },
    shouldShowContextMenu() {
      return !(this.isFailed || this.isPending);
    },
    contentAttributes() {
      return this.data.content_attributes || {};
    },
    sender() {
      return this.data.sender || {};
    },
    showAgentAvatar() {
      return this.isOutgoing || this.isTemplate;
    },
    isBotMessage() {
      return (
        (this.isOutgoing || this.isTemplate) &&
        !this.data.private &&
        this.isSentByBot
      );
    },
    status() {
      return this.data.status;
    },
    createdAt() {
      return this.contentAttributes.external_created_at || this.data.created_at;
    },
    hasText() {
      return !!this.data.content;
    },
    message() {
      return this.formatMessage(
        this.data.content || '',
        this.isATweet,
        this.data.private,
        this.isAWhatsAppChannel
      );
    },
    tooltipForSender() {
      const { message_type: messageType } = this.data;
      const showTooltip =
        messageType === MESSAGE_TYPE.OUTGOING ||
        messageType === MESSAGE_TYPE.TEMPLATE;
      return showTooltip
        ? {
            content: `${this.$t('CONVERSATION.SENT_BY')} ${
              this.senderNameForAvatar
            }`,
          }
        : false;
    },
    senderNameForAvatar() {
      if (this.isOutgoing || this.isTemplate) {
        const { available_name: availableName, name } = this.sender || {};
        return availableName || name || this.$t('CONVERSATION.BOT');
      }
      return '';
    },
    contextMenuEnabledOptions() {
      return {
        scopeTiktok: this.isATiktok,
        copy: this.hasText,
        delete:
          this.hasText &&
          (this.deleteable || this.data.private) &&
          !this.contentAttributes?.story_id &&
          this.hasInboxPermission('Inbox delete message'),
        cannedResponse:
          this.isOutgoing &&
          this.hasText &&
          this.hasCreatePermission('Canned Responses'),
        replyTo: !this.data.private && this.inboxSupportsReplyTo,
        remoteAssignFile: this.assignable,
      };
    },
    isSentByBot() {
      if (this.isPending || this.isFailed) return false;
      return !this.sender.type || this.sender.type.includes('bot');
    },
    storySender() {
      return this.contentAttributes.story_sender || null;
    },
    storyId() {
      return this.contentAttributes.story_id || null;
    },
    storyUrl() {
      return this.contentAttributes.story_url || null;
    },
    storyReply() {
      return this.storyUrl && this.hasInstagramStory;
    },
    inReplyToMessageId() {
      return this.data.content_attributes?.in_reply_to;
    },
    translatedMessage() {
      if (
        !this.targetLanguage ||
        !this.data?.content_attributes?.translations?.[this.targetLanguage]
      ) {
        return null;
      }
      return this.data.content_attributes.translations[this.targetLanguage];
    },
    externalError() {
      return this.contentAttributes.external_error || null;
    },
    isAnIntegrationMessage() {
      return this.contentType === 'integrations';
    },
    isLiked() {
      return (
        this.d99dFeatures.fb2Featureable && this.contentAttributes.is_liked
      );
    },
    reaction() {
      return this.contentAttributes.reaction_icon;
    },
  },
  watch: {
    data() {
      this.hasImageError = false;
      this.hasImgStoryError = false;
    },
    async showContextMenu(val) {
      if (!val || !this.d99dFeatures.fb2Featureable) return;
      const { inbox_id, source_id } = this.data;
      if (this.d99dEnables.empty) {
        this.d99dEnables = {
          showLike: !this.contentAttributes.is_liked,
          showUnlike: this.contentAttributes.is_liked,
          showHide: this.isIncoming && !this.contentAttributes.is_hidden,
          showUnhide: this.isIncoming && this.contentAttributes.is_hidden,
        };
      } else if (this.d99dEnables.loading !== false) {
        this.d99dEnables = { loading: true };
      }
      const result = await this.$store.dispatch('getCommentInfo', {
        inbox_id,
        source_id,
      });
      const {
        user_likes: isLiked,
        can_like: canLike,
        can_hide: canHide,
        is_hidden: isHidden,
        can_reply_privately: canReplyPrivately,
      } = result;
      this.d99dEnables = {
        loading: false,
        showLike: canLike && !isLiked,
        showUnlike: canLike && isLiked,
        showHide: canHide && !isHidden,
        showUnhide: canHide && isHidden,
        showPrivateReply: canReplyPrivately,
      };
    },
  },
  mounted() {
    this.hasImageError = false;
    this.hasImgStoryError = false;
    bus.$on(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL, this.closeContextMenu);
    this.$nextTick(() => {
      const telLinks = this.$refs.message?.querySelectorAll('a[href^="tel:"]');
      const emailLinks =
        this.$refs.message?.querySelectorAll('a[href^="mailto:"]');
      telLinks?.forEach(link => {
        link.addEventListener('click', this.handleTelLinkClick);
      });
      emailLinks?.forEach(link => {
        link.addEventListener('click', this.handleEmailLinkClick);
      });
      // this.confirmLinkHandler = this.onConfirmOpenLink.bind(this);
      // const externalLinks = this.$refs.message?.querySelectorAll(
      //   'a[href]:not([href^="#"]):not([href^="tel:"]):not([href^="mailto:"])'
      // );
      // externalLinks?.forEach(link => {
      //   link.addEventListener('click', this.confirmLinkHandler);
      // });
    });
  },
  beforeDestroy() {
    bus.$off(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL, this.closeContextMenu);
    clearTimeout(this.higlightTimeout);
    if (this.confirmLinkHandler) {
      const externalLinks = this.$refs.message?.querySelectorAll(
        'a[href]:not([href^="#"]):not([href^="tel:"]):not([href^="mailto:"])'
      );
      externalLinks?.forEach(link => {
        link.removeEventListener('click', this.confirmLinkHandler);
      });
      this.confirmLinkHandler = null;
    }
  },
  methods: {
    openContextMenu(e) {
      const shouldSkipContextMenu =
        e.target?.classList.contains('skip-context-menu') ||
        e.target?.tagName.toLowerCase() === 'a';
      if (shouldSkipContextMenu || getSelection().toString()) return;
      e.preventDefault();
      if (e.type === 'contextmenu') {
        this.$track(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
      }

      this.contextMenuPosition = {
        x: e.pageX || e.clientX,
        y: e.pageY || e.clientY,
      };
      this.showContextMenu = true;
    },
    openMoreMenu(e) {
      this.$track(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
      const rect = e.currentTarget.getBoundingClientRect();
      this.contextMenuPosition = {
        x: e.pageX || e.clientX,
        y: rect.bottom + window.scrollY,
      };
      this.showContextMenu = true;
    },
    closeContextMenu() {
      this.showContextMenu = false;
      this.contextMenuPosition = { x: null, y: null };
    },
    handleReplyTo() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const { conversation_id: conversationId, id: replyTo } = this.data;
      LocalStorage.updateJsonStore(replyStorageKey, conversationId, replyTo);
      bus.$emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.data);
    },
    handleRemoteAssignFile() {
      bus.$emit(BUS_EVENTS.SET_PLATFORM_ASSIGN_FILE, this.data);
    },
    async handleD99dSelection(option) {
      const { inbox_id, source_id } = this.data;
      const payload = { inbox_id, source_id };
      this.d99dEnables = {};
      try {
        switch (option) {
          case 'like':
            await this.$store.dispatch('createLike', payload);
            break;
          case 'unlike':
            await this.$store.dispatch('deleteLike', payload);
            break;
          case 'hide':
            await this.$store.dispatch('hide', payload);
            break;
          case 'unhide':
            await this.$store.dispatch('unhide', payload);
            break;
          case 'tiktokUnlike':
            await this.$store.dispatch('sendTiktokLike', {
              ...payload,
              liked: false,
            });
            break;
          case 'tiktokLike':
            await this.$store.dispatch('sendTiktokLike', {
              ...payload,
              liked: true,
            });
            break;
          case 'tiktokUnhide':
            await this.$store.dispatch('sendTiktokHide', {
              ...payload,
              hide: false,
            });
            break;
          case 'tiktokHide':
            await this.$store.dispatch('sendTiktokHide', {
              ...payload,
              hide: true,
            });
            break;
          case 'private_reply':
            this.d99dFeatures.handlePrivateReply(this.data);
            return;
          default:
            return;
        }
        this.showAlert(this.$t('CONVERSATION.SUCCESS_FB_COMMENT_UPDATE'));
      } catch (error) {
        this.showAlert(this.$t('CONVERSATION.FAIL_FB_COMMENT_UPDATE'));
      }
    },
    handleShowTranslate(language) {
      this.targetLanguage = language;
      const {
        id: messageId,
        conversation_id: conversationId,
        content_attributes: contentAttributes,
      } = this.data;
      if (
        !contentAttributes.translations ||
        !contentAttributes.translations[language]
      ) {
        this.$store.dispatch('translateMessage', {
          conversationId,
          messageId,
          targetLanguage: language || 'en',
        });
      }
    },
    handleHideTranslate() {
      this.targetLanguage = null;
    },
    setupHighlightTimer(messageId) {
      if (Number(messageId) !== Number(this.data.id)) return;
      this.showBackgroundHighlight = true;
      const HIGHLIGHT_TIMER = 1000;
      this.higlightTimeout = setTimeout(() => {
        this.showBackgroundHighlight = false;
      }, HIGHLIGHT_TIMER);
    },
    onConfirmOpenLink(event) {
      try {
        const account = this.$store.getters['accounts/getAccount'](
          this.$store.getters.getCurrentAccountId
        );
        if (!account || !account.warn_on_external_links) return;
        const el = event.currentTarget;
        const href = el.getAttribute('href') || el.href;
        if (!href) return;
        if (
          href.startsWith('#') ||
          href.startsWith('mailto:') ||
          href.startsWith('tel:')
        )
          return;
        event.preventDefault();
        this.pendingLinkHref = href;
        this.showLinkConfirmModal = true;
      } catch (e) {
        console.error(e);
      }
    },
    onConfirmOpenLinkAction() {
      this.showLinkConfirmModal = false;
      if (this.pendingLinkHref) {
        window.open(this.pendingLinkHref, '_blank', 'noopener noreferrer');
      }
      this.pendingLinkHref = null;
    },
    onCancelOpenLink() {
      this.showLinkConfirmModal = false;
      this.pendingLinkHref = null;
    },
    handleTelLinkClick(event) {
      event.preventDefault();
      const phoneNumber = event.target.href.replace('tel:', '');
      const phoneNumberTxt = event.target.textContent;
      bus.$emit(BUS_EVENTS.START_NEW_CONVERSATION, {
        phoneNumber,
        name: this.getNameForPhone(phoneNumberTxt),
      });
    },
    handleEmailLinkClick(event) {
      event.preventDefault();
      const email = event.target.href.replace('mailto:', '');
      bus.$emit(BUS_EVENTS.START_NEW_CONVERSATION, {
        email,
        name: this.getNameForPhone(email),
      });
    },
    getNameForPhone(phoneNumber = '') {
      const content = this.data.content.replace(/\\n/gm, '\n ');
      const escapedPhoneNumber = phoneNumber.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&'
      );
      const regex = new RegExp(
        `([א-תA-Za-z]+(?:[א-תA-Za-z0-9]*\\s+)*)(?=${escapedPhoneNumber})`
      );
      const match = content.match(regex);
      return match ? match[1].trim() : '';
    },
    onImageLoadError() {
      this.hasImageError = true;
    },
    onStoryLoadError() {
      this.hasImgStoryError = true;
    },
    isAttachmentImageVideoAudio(fileType) {
      return ['image', 'audio', 'video'].includes(fileType);
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .message-text {
    overflow-wrap: anywhere;
    p {
      margin: 0;
    }
  }

  .bubble--bot {
    .message-text,
    .message-text p,
    .message-text a {
      color: #fff;
    }

    .message-text a {
      text-decoration: underline;
    }

    .message-text--metadata {
      > span[dir='ltr'],
      > span > .material-symbols-outlined,
      > span.material-symbols-outlined:not([class*='EF4444']),
      > a .material-symbols-outlined {
        color: #fff;
        opacity: 0.9;
      }
    }
  }
}
</style>
